import {makeAutoObservable, runInAction} from 'mobx';

import {CaptchaStore} from '@yourcoach/captcha';
import {i18n} from '@yourcoach/i18n';

import type {Destructible} from '../../../utils/store';
import type {Config, SendCodeError, SendTo} from './types';

import {AuthStore, authStore} from '../../../api/auth';
import {logger} from '../../../utils/logger';

/**
 * @description
 * The store contains logic of sending a confirmation code
 * during login/signup or adding an additional account
 */
export class SendCodeStore implements Destructible {
  inProgress = false;
  private captcha: CaptchaStore;
  private forAccount: Config['forAccount'];
  private isAuthFlow: Config['isAuthFlow'];
  private isLoginFlow: Config['isLoginFlow'];
  private linkTo: Config['linkTo'];
  private platform: Config['platform'];

  constructor(config: Config) {
    this.isAuthFlow = config.isAuthFlow;
    this.isLoginFlow = config.isLoginFlow;
    this.platform = config.platform;
    this.forAccount = config.forAccount;
    this.linkTo = config.linkTo;

    this.captcha = new CaptchaStore(config.captchaKey, {
      shouldBeMocked: config.mockCaptcha,
    });

    makeAutoObservable(this, undefined, {
      autoBind: true,
    });
  }

  destruct() {}

  async sendCode(to: SendTo) {
    try {
      runInAction(() => {
        this.inProgress = true;
      });

      let recaptchaToken = await this.captcha.getToken(
        AuthStore.CHECK_USER_CAPTCHA_ACTION,
      );

      let result = await authStore.checkUser(
        {...to, platform: this.platform},
        recaptchaToken,
      );

      if (this.isLoginFlow && !result.user_exists) {
        throw {
          code: 'account_not_exist_error',
          message: i18n.t(
            'shared:confirmation_code.account_not_exist_error.message',
          ),
          title: i18n.t(
            'shared:confirmation_code.account_not_exist_error.title',
          ),
        } as SendCodeError;
      }

      if (!this.isAuthFlow && result.user_exists) {
        throw {
          code: 'account_exists_error',
          title: i18n.t('shared:confirmation_code.account_exists_error.title'),
        } as SendCodeError;
      }

      recaptchaToken = await this.captcha.getToken(
        AuthStore.SEND_CODE_CAPTCHA_ACTION,
      );

      result = await authStore.sendCode(
        {...to, forAccount: this.forAccount, linkTo: this.linkTo},
        recaptchaToken,
      );

      runInAction(() => {
        this.inProgress = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.inProgress = false;
      });

      logger.error(error);

      throw {
        ...error,
        title:
          error.title || i18n.t('shared:confirmation_code.send_code_error'),
      } as SendCodeError;
    }
  }
}
